<template>
  <template v-for="(item, index) in itemList" :key="index">
    <el-tooltip
      v-if="!item.hidden && (item.label || item.placeholder || item.slot)"
      popper-class="custom_tooltip"
      :effect="item.tooltip?.effect ?? 'gray'"
      :disabled="!item.tooltip?.content"
      :enterable="false"
      :hide-after="0"
      :trigger="item.tooltip?.trigger ?? 'hover'"
      :placement="item.tooltip?.placement ?? 'bottom'"
    >
      <template #content>
        <div class="tooltip-content">
          {{ item.tooltip?.content }}
        </div>
      </template>
      <div>
        <a-form-item
          class="custom-form-item"
          :class="{
            [item.class]: item.class,
            'is-relative': item.afterTooltip,
          }"
          :style="{
            '--maxWidth': item.maxWidth || '100%',
          }"
          :show-colon="item.showColon ?? props.showColon ?? true"
          :field="item.field"
          :required="item.required && (item.validate ?? true)"
          :hide-asterisk="item.hideAsterisk"
          :rules="[
            ...(item.rules || []),
            item.type === 'input'
              ? {
                  trigger: 'input',
                  validator: (value, cb) => {
                    cb($filters.isAllSpace(value) ?? new Error());
                  },
                }
              : {},
          ]"
          :validate-trigger="validateTrigger(item.type)"
        >
          <!-- label -->
          <template #label>
            <!-- label复选 -->
            <a-checkbox
              class="custom-label-item-checkbox"
              v-if="item.labelChecked"
              :disabled="item.labelDisabled || false"
              v-model="model[item.labelField]"
              @change="emit('checkChange', item.labelField)"
            >
              {{ item.label }}
            </a-checkbox>
            <!-- 不自动校验 -->
            <span v-else-if="item.validate === false && item.required">
              <required-dom></required-dom>
              <span>{{ item.label }}</span>
            </span>
            <!-- 普通label -->
            <span v-else>{{ item.label }}</span>
          </template>
          <!-- 文本框 -->
          <template v-if="item.type === 'input'">
            <a-input
              :disabled="item.disabled || false"
              v-model="model[item.field]"
              :ref="(ev) => setRefs(ev, item.field)"
              :placeholder="setPlaceholder(item) || item.label"
              :type="item.inputType || 'text'"
              allowClear
              @input="emitEvent('input', item)"
              @change="emitEvent('change', item)"
            >
            </a-input>
          </template>
          <!-- 数字（不带调节按钮） -->
          <template v-else-if="item.type === 'inputNumber'">
            <a-input-number
              :disabled="item.disabled || false"
              v-model="model[item.field]"
              hide-button
              :placeholder="setPlaceholder(item) || '0'"
              :min="0"
              :max="item.max || Infinity"
              @input="emitEvent('input', item)"
              @blur="handleToSlice(item)"
              @change="emitEvent('change', item)"
            >
              <template #prefix v-if="item.prefix">
                {{ item.prefix }}
              </template>
            </a-input-number>
          </template>
          <!-- 数字（带调节按钮） -->
          <template v-else-if="item.type === 'number'">
            <a-input-number
              :disabled="item.disabled || false"
              v-model="model[item.field]"
              :placeholder="setPlaceholder(item) || '0'"
              :min="0"
              :max="item.max || Infinity"
              @input="emitEvent('input', item)"
              @blur="handleToSlice(item)"
              @change="emitEvent('change', item)"
            >
            </a-input-number>
          </template>
          <!-- 下拉 -->
          <template v-else-if="item.type === 'select'">
            <c-select
              :class="{
                readonly: item.disabled || !item.optionsKey,
              }"
              v-model="model[item.field]"
              :disabled="item.disabled || false"
              :placeholder="setPlaceholder(item) || item.label"
              :default-value="item.defaultValue"
              :options="item.options || hisCodeList[item.optionsKey]"
              :field-names="item.fieldNames"
              :allowClear="item.allowClear ?? true"
              :allow-search="item.allowSearch ?? true"
              @change="emitEvent('change', item)"
              @popup-show="emitEvent('selectVisibleChange', item)"
            >
            </c-select>
          </template>
          <!-- 患者名称下拉，可手填 -->
          <template v-else-if="item.type === 'patientInput'">
            <c-pati-input
              v-model="model[item.field]"
              :placeholder="setPlaceholder(item) || item.label"
              :allowClear="item.allowClear ?? false"
              @input="emitEvent('input', item)"
              @change="(ev) => emitEvent('change', item, ev)"
              @clear="emitEvent('clear', item)"
            ></c-pati-input>
          </template>
          <!-- 单日日期 -->
          <template v-else-if="item.type === 'datePicker'">
            <a-date-picker
              :disabled="item.disabled || false"
              v-model="model[item.field]"
              :placeholder="setPlaceholder(item) || item.label"
              :disabled-date="item.disabledDate"
              :format="item.format || $filters.dateFormat"
              :value-format="item.format || $filters.dateFormat"
              @change="emitEvent('change', item)"
              allowClear
            />
          </template>
          <!-- 单位下拉 -->
          <template v-else-if="item.type === 'unitInput'">
            <c-unit-input
              v-model="model[item.field]"
              :disabled="item.disabled || false"
              :customClass="item.customClass"
              :bodyCustomClass="item.bodyCustomClass"
              :placeholder="setPlaceholder(item) || item.label"
              :options="item.options || hisCodeList[item.optionsKey]"
              :field-names="item.fieldNames"
              :allowClear="item.allowClear ?? true"
              :align="item.align"
              :autoFitPopupWidth="item.autoFitPopupWidth"
              :number="item.number"
              @change="emitEvent('change', item)"
              @popup-visible-change="
                (e) => emitEvent('unitVisibleChange', item, e)
              "
            >
              <template v-if="$slots.unitFooter" #footer>
                <slot name="unitFooter"></slot>
              </template>
            </c-unit-input>
          </template>
          <!-- 其他slot元素 -->
          <template v-else>
            <slot :name="item.slot" :row="item"></slot>
          </template>
          <!-- 后置元素 -->
          <template v-if="item.after">
            <span class="after_text">
              {{ item.after }}
            </span>
          </template>
          <!-- 后置tooltip -->
          <template v-if="item.afterTooltip && item.afterTooltip?.content">
            <el-tooltip
              popper-class="custom_tooltip"
              :effect="item.afterTooltip?.effect ?? 'gray'"
              :disabled="!item.afterTooltip?.content"
              :trigger="item.afterTooltip?.trigger ?? 'hover'"
              :placement="item.afterTooltip?.placement ?? 'bottom'"
            >
              <icon-question-circle-fill class="tip_icon" />
              <template #content>
                <div class="tooltip-content">
                  {{ item.afterTooltip?.content }}
                </div>
              </template>
            </el-tooltip>
          </template>
        </a-form-item>
      </div>
    </el-tooltip>
    <div v-else-if="!item.hidden"></div>
  </template>
</template>

<script setup>
import { onBeforeMount, reactive, ref, watch } from 'vue';
import _ from 'lodash';
import CPatiInput from '@/components/patiNamePop/index.vue';
import CUnitInput from '@/components/selectBlock/unitInput.vue';
import requiredDom from './required.vue';
import { getHisCode } from '@/utils/util.js';

const emit = defineEmits([
  'input',
  'change',
  'clear',
  'drugChange',
  'checkChange',
  'selectVisibleChange',
  'unitVisibleChange',
]);
/**
 * @slot unitFooter 单位下拉时的footer插槽
 */
const props = defineProps({
  /**
   * @param {Array} itemList 绑定数据
   * @param {String} itemList[].field 字段名
   * @param {String} itemList[].label 标签名
   * @param {String} itemList[].labelField 标签字段名
   * @param {Boolean} itemList[].labelChecked 标签是否可复选
   * @param {String} itemList[].type 类型
   * @param {Boolean} itemList[].showColon 是否显示冒号
   * @param {Boolean} itemList[].required 是否必填
   * @param {Boolean} itemList[].hideAsterisk 是否隐藏星号
   * @param {Boolean} itemList[].hidden 是否隐藏
   * @param {Boolean} itemList[].labelDisabled 功能标签是否禁用
   * @param {Boolean} itemList[].disabled 是否禁用
   * @param {String} itemList[].maxWidth 元素最大宽度
   * @param {String} itemList[].after 后缀
   * @param {String | Function} itemList[].placeholder 占位符
   * @param {Array} itemList[].rules 校验规则（优先级高于 form 的 rules）
   * @param {Boolean} itemList[].validate 是否参与自动校验
   *
   * @param {String} itemList[].inputType 文本框类型——type为input时有效
   * @param {String} itemList[].prefix 前缀——type为inputNumber时有效
   * @param {Number} itemList[].precision 限制位数——type为number/inputNumber有效
   * @param {Number} itemList[].max 最大值——type为inputNumber/number有效
   *
   * @param {Object} itemList[].params 参数——type为select时有效
   *
   * @param {Function} itemList[].disabledDate 时间禁用——type为datePicker时有效
   *
   * @param {String} itemList[].optionsKey 字典key——type为select时有效
   * @param {Object} itemList[].fieldNames 字段名——type为select/unitInput时有效
   * @param {String} itemList[].defaultValue 默认值——type为select时有效
   * @param {Boolean} itemList[].allowClear 是否显示清除按钮——type为select/patientInput/unitInput时有效
   * @param {Boolean} itemList[].allowSearch 是否允许搜索——type为select时有效
   *
   * @param {Number} itemList[].customClass 自定义class——type为unitInput时生效
   * @param {Number} itemList[].bodyCustomClass 自定义body class——type为unitInput时生效
   * @param {Number} itemList[].number 单位下拉一排显示的个数——type为unitInput时生效
   * @param {Number} itemList[].align 排列方向——type为unitInput时生效
   * @param {Number} itemList[].autoFitPopupWidth 下拉是否和选择器一样的宽度——type为unitInput时生效
   *
   * @param {Object} itemList[].tooltip
   * @param {String} itemList[].tooltip.effect 主题 dark/light/gray
   * @param {String} itemList[].tooltip.content 提示内容
   * @param {String} itemList[].tooltip.placement 提示位置
   *
   * @param {Object} itemList[].afterTooltip
   * @param {String} itemList[].afterTooltip.effect 主题 dark/light/gray
   * @param {String} itemList[].afterTooltip.content 提示内容
   * @param {String} itemList[].afterTooltip.placement 提示位置
   *
   * @param {String} itemList[].slot 插槽名称
   */
  itemList: {
    type: Array,
    default: [],
    required: true,
  },
  model: {
    type: Object,
    default: {},
    required: true,
  },
  showColon: {
    type: Boolean,
    default: true,
  },
});

// 字典数据
const hisCodeList = reactive({});
// 是否在加载字典数据
let hisLoading = async () => await new Promise((_resolve) => {});

// 判断校验方式
const validateTrigger = (type) => {
  let trigger = [];
  switch (type) {
    case 'input':
      trigger = ['blur'];
      break;
    case 'select':
      trigger = ['change'];
      break;
    default:
      trigger = ['change', 'blur'];
      break;
  }
  return trigger;
};
// 数字最大输入小数位数截断
const handleToSlice = (item) => {
  if (item.precision >= 0) {
    props.model[item.field] = Number(props.model[item.field]).toSlice(
      item.precision,
    );
  }
};
// 占位符
const setPlaceholder = (item) => {
  if (!item.placeholder) return false;
  if (item.placeholder instanceof Function) {
    return item.placeholder(item.label);
  }
  return item.placeholder;
};

// 提交事件
const emitEvent = (event, item, other) => {
  emit(event, item.field, item, other);
};

// 字典参数
watch(
  () => props.itemList,
  () => {
    let hisKeyList = [];
    props.itemList.map((item) => {
      if (
        (item.type === 'select' || item.type === 'unitInput') &&
        item.hidden !== true
      ) {
        hisKeyList.push({
          key: item.optionsKey,
          params: item.params,
        });
      }
    });
    if (hisKeyList.length === 0) {
      hisLoading = async () => await Promise.resolve();
      return false;
    }
    const map = hisKeyList.map((item) => {
      return new Promise(async (resolve) => {
        if (item.params === false) {
          hisCodeList[item.key] = [];
          return;
        }
        const data = await getHisCode(item.key, item.params);
        hisCodeList[item.key] = data;
        resolve(data);
      });
    });
    hisLoading = async () => await Promise.all(map);
  },
  { deep: true, immediate: true },
);

const refs = reactive({});
const setRefs = (ev, field) => {
  refs[field] = ev;
};

onBeforeMount(function () {
  props.itemList.map((item) => {
    if (!item.field && !item.slot) {
      const error = new Error(
        `label为 ${item.label} 的field为空或undfined，请检查数据！`,
      );
      console.warn(item, error);
    }
  });
});

defineExpose({
  getRefs() {
    return refs;
  },
  async getHisCode(code) {
    await hisLoading();
    return code ? hisCodeList[code] : hisCodeList;
  },
});
</script>

<style lang="scss" scoped>
@import '@/assets/css/mixin.scss';
.custom-form-item {
  align-items: center;
  &.is-relative {
    position: relative;
  }
  .custom-label-item-checkbox {
    @include FontSize_x14;
    ::v-deep(.arco-checkbox-label) {
      color: var(--color_darkgray);
    }
  }
  ::v-deep(.arco-form-item-label) {
    display: flex;
    align-items: center;
  }
  ::v-deep(.arco-form-item-wrapper-col) {
    max-width: var(--maxWidth);
  }
}
.after_text {
  @include FontSize_x14;
  margin-left: 4px;
  white-space: nowrap;
  color: var(--color_darkgray);
}
.tip_icon {
  position: absolute;
  right: -24px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 22px;
  color: var(--color_brand);
}
</style>
